import styled from '@emotion/styled';
import Link from '@ep/components/Link';
import theme from '@ep/theme';
import useQueryParam from '@ep/utils/useQueryParams';
import React, { useEffect, useLayoutEffect, useState } from 'react';

export interface SectionMenuItem {
  link: string;
  label: string;
  parent?: boolean;
  children?: ChildrenObject[];
}

interface ChildrenObject {
  link: string;
  label: string;
}

export interface SectionMenuData {
  data: SectionMenuItem[];
}

export interface SectionMenuProps {
  pathname: string;
  menu: SectionMenuData;
}

interface StyledContainerProp {
  scrollingDown: boolean;
}

const StyledContainer = styled.div<StyledContainerProp>`
  width: 100%;
  position: fixed;
  z-index: ${theme.zIndex.sectionMenu};
  top: ${({ scrollingDown }) => (scrollingDown ? '6rem' : '10rem')};
  transition: 0.25s;
  ${theme.mediaQueries.desktopMenuBreak} {
    top: 6rem;
  }
  ${theme.mediaQueries.tablet} {
    top: 5rem;
  }
  ${theme.mediaQueries.mobile} {
    top: 3.5rem;
    font-size: 0.8rem;
  }
`;

interface SectionMenuContainerProp {
  isTop: boolean;
}

const SectionMenuContainer = styled.div<SectionMenuContainerProp>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0.5rem;
  flex-wrap: wrap;
  background-color: ${({ isTop }) =>
    isTop ? theme.colors.white85 : theme.colors.white};
`;

const Spacer = styled.span`
  padding: 0 1rem;
  ${theme.mediaQueries.mobile} {
    padding: 0 0.5rem;
  }
`;

export interface StyledLinkProp {
  currentPage: boolean;
}

const StyledLink = styled(Link)<StyledLinkProp>`
  color: ${({ currentPage }) =>
    currentPage ? theme.colors.mediumBlue : theme.colors.darkBlue};
  transition: 0.25s;
  font-size: 1rem;
  &:hover {
    transition: 0.25s;
    text-decoration: none;
    color: ${theme.colors.skyBlue};
  }
`;

const SectionMenuModule = ({ menu, pathname }: SectionMenuProps) => {
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [bodyOffset, setBodyOffset] = useState({ top: 0 });
  const [scrollY, setScrollY] = useState(bodyOffset.top);
  const [scrollDirection, setScrollDirection] = useState(false);
  // smChildrenOpen = SubMenu Children Open
  const [smChildren, setSmChildren] = useState<{
    open: boolean;
    parentIndex: number;
  }>({ open: false, parentIndex: 0 });
  const [menuType, setMenuType] = useQueryParam('menu', '');
  const listener = (e) => {
    setBodyOffset(document.body.getBoundingClientRect());
    setScrollY(-bodyOffset.top);
    setScrollDirection(lastScrollTop > -bodyOffset.top ? false : true);
    setLastScrollTop(-bodyOffset.top);
  };

  let finalMenu = menu;
  if (menu.length === 1) {
    finalMenu = menu[0];
  }
  if (menu.length > 1) {
    if (!menuType) {
      finalMenu = menu[0];
    } else {
      menu.map((type) => {
        if (type.data[0].label === menuType) {
          finalMenu = type;
        }
      });
    }
  }

  const hasChildren = (data, slug) => {
    const menuArray = data.data;
    let linkHasChildren: boolean = false;
    for (let i = 0; i < menuArray.length; i++) {
      if (menuArray[i].link === slug) {
        linkHasChildren = menuArray[i].children ? true : false;
      } else if (menuArray[i].children) {
        const children: { link: string; label: string }[] =
          menuArray[i].children;
        for (let j = 0; j < children.length; j++) {
          if (children[j].link === slug) {
            linkHasChildren = true;
          }
        }
      }
    }
    return linkHasChildren;
  };

  const parentIndex = (data, slug) => {
    const menuArray = data.data;
    let parentIndex: number = 0;
    for (let i = 0; i < menuArray.length; i++) {
      if (menuArray[i].link === slug) {
        parentIndex = i;
      } else if (menuArray[i].children) {
        const childrenArray = menuArray[i].children;
        for (let j = 0; j < childrenArray.length; j++) {
          const child = childrenArray[j].link;
          if (child === slug) {
            parentIndex = i;
          }
        }
      }
    }
    return parentIndex;
  };

  useLayoutEffect(() => {
    setSmChildren({
      open: hasChildren(finalMenu, pathname),
      parentIndex: parentIndex(finalMenu, pathname),
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', listener);
    return () => {
      window.removeEventListener('scroll', listener);
    };
  });

  // const sendToGoogleAnalytics = (label: string) => {
  //   dataLayer.push({ event: label });
  // };
  return (
    <StyledContainer scrollingDown={scrollDirection}>
      {smChildren.open ? (
        <SectionMenuContainer isTop={scrollY < 10}>
          <StyledLink
            currentPage={true}
            key={finalMenu.data[0]?.link}
            to={finalMenu.data[0]?.link}
            onClick={() => {
              setSmChildren({ ...smChildren, open: !smChildren.open });
            }}
          >
            {finalMenu.data[0]?.label}
          </StyledLink>
          <Spacer>|</Spacer>
          <StyledLink
            currentPage={true}
            key={finalMenu.data[smChildren.parentIndex]?.link}
            to={
              finalMenu.data[smChildren.parentIndex]?.parent
                ? finalMenu.data[smChildren.parentIndex]?.link +
                  '?menu=' +
                  finalMenu.data[smChildren.parentIndex]?.label
                : finalMenu.data[smChildren.parentIndex]?.link
            }
            onClick={(event) => {
              if (pathname !== finalMenu.data[smChildren.parentIndex]?.link) {
                setSmChildren({ ...smChildren, open: true });
              }
            }}
          >
            {finalMenu.data[smChildren.parentIndex]?.label}
          </StyledLink>
          <Spacer>&gt;</Spacer>
          {finalMenu.data[smChildren.parentIndex]?.children?.map(
            (link, index) => (
              <div key={`${link.link}__${index}childrenContainer`}>
                {index === 0 ? '' : <Spacer>|</Spacer>}
                <StyledLink
                  currentPage={pathname === link.link}
                  key={link.link}
                  to={
                    link.parent
                      ? link.link + '?menu=' + finalMenu.data[0].label
                      : link.link
                  }
                  // onClick={(event) => {
                  //   sendToGoogleAnalytics(
                  //     'Section menu item clicked - ' + link.label,
                  //   );
                  // }}
                >
                  {link.label}
                </StyledLink>
              </div>
            ),
          )}
        </SectionMenuContainer>
      ) : (
        <SectionMenuContainer isTop={scrollY < 10}>
          {finalMenu.data.map((link, index) => (
            <div key={`${link.link}__${index}`}>
              {index === 0 ? '' : <Spacer>|</Spacer>}
              <StyledLink
                currentPage={pathname === link.link}
                key={link.link}
                to={
                  link.parent
                    ? link.link + '?menu=' + finalMenu.data[0].label
                    : link.link
                }
                onClick={(event) => {
                  if (link.children) {
                    setSmChildren({ open: true, parentIndex: index });
                  }
                  // sendToGoogleAnalytics(
                  //   'Section menu item clicked - ' + link.label,
                  // );
                }}
              >
                {link.label}
              </StyledLink>
            </div>
          ))}
        </SectionMenuContainer>
      )}
    </StyledContainer>
  );
};

export default SectionMenuModule;
